/**
 * Schichten — die klassische Pflege von Schichttypen und Schichtregeln.
 *
 * Umbau auf die Leitstand-Optik (18.08.2026): gleiche Schritte, gleiche
 * Knöpfe, gleiche Wirkung — nur ohne feste Farbwerte. Zustand kommt
 * ausschliesslich aus den Token (lauf, stand, stoer), damit ein Farbwechsel
 * an einer einzigen Stelle möglich bleibt (styles/index.css).
 *
 * Der Schichtplan-Kalender liegt unter /admin/shifts; diese Seite pflegt die
 * Regeln, aus denen er entsteht.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import {
  Karte,
  Kennung,
  Kopfzelle,
  Notiz,
  Tabelle,
  Zelle,
  Zustand,
} from '../../components/leitstand';
import { formatBerlinDateISO } from '../../utils/berlinDate';
import { getRuleRole, formatValidity } from '../../utils/shiftRuleRole';

// =============================================================================
// Gemeinsame Klassen — ein Ort für Eingabefelder und Knöpfe
// =============================================================================

const EINGABE =
  'rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-sm text-tinte focus:border-akzent focus:outline-none disabled:opacity-60';
const KNOPF = 'rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50';
const KNOPF_PRIMAER = `${KNOPF} border border-akzent bg-akzent text-akzent-satz`;
const KNOPF_LEISE = `${KNOPF} border border-linie-2 bg-flaeche text-tinte-2`;

// =============================================================================
// Types
// =============================================================================

interface ShiftType {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface ShiftRule {
  id: string;
  code: string;
  name: string;
  shiftType: { id: string; code: string; name: string };
  weekdays: number[];
  startTime: string;
  endTime: string;
  validFrom: string | null;
  validUntil: string | null;
  priority: number;
  isActive: boolean;
}

interface CurrentShiftResponse {
  hasActiveShift: boolean;
  ruleName: string | null;
  shiftTypeName: string | null;
  shiftDateStr: string | null;
  startTime: string | null;
  endTime: string | null;
}

// =============================================================================
// Helpers
// =============================================================================

const sortAlpha = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
};

const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Mo',
  2: 'Di',
  3: 'Mi',
  4: 'Do',
  5: 'Fr',
  6: 'Sa',
  7: 'So',
};

const WEEKDAY_FULL: Record<number, string> = {
  1: 'Montag',
  2: 'Dienstag',
  3: 'Mittwoch',
  4: 'Donnerstag',
  5: 'Freitag',
  6: 'Samstag',
  7: 'Sonntag',
};

function formatWeekdays(weekdays: number[]): string {
  return [...weekdays]
    .sort((a, b) => a - b)
    .map((d) => WEEKDAY_LABELS[d] || '?')
    .join(', ');
}

function formatTime(start: string, end: string): string {
  const isOvernight = end < start;
  return `${start} - ${end}${isOvernight ? ' (+1)' : ''}`;
}

/** Beschriftetes Wertepaar in den Ergebniskarten oben. */
function Feld({ marke, children }: { marke: string; children: ReactNode }) {
  return (
    <>
      <dt className="self-baseline font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
        {marke}
      </dt>
      <dd className="text-tinte-2">{children}</dd>
    </>
  );
}

/**
 * Basis oder Ausnahme. Die Ausnahme trägt die Warnfarbe, weil sie andere
 * Regeln aussticht — man soll sie in einer Liste sofort finden.
 */
function RollenKennung({ rolle }: { rolle: 'base' | 'override' }) {
  if (rolle === 'base') return <Kennung>Basis</Kennung>;
  return (
    <span className="rounded-[4px] bg-flaeche-2 px-[7px] py-[3px] font-zahl text-[10px] tracking-[0.08em] text-stand">
      Ausnahme
    </span>
  );
}

/** Aktiv oder nicht — überall gleich gesetzt. */
function ZustandsZeichen({ aktiv }: { aktiv: boolean }) {
  return aktiv ? (
    <Zustand art="gut">aktiv</Zustand>
  ) : (
    <span className="font-zahl text-[11px] text-tinte-3">inaktiv</span>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export default function ShiftsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Refs für Scroll-Targets
  const typesSectionRef = useRef<HTMLElement>(null);
  const rulesSectionRef = useRef<HTMLElement>(null);

  // Highlight State (für temporäres Highlighting eines Eintrags)
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  // Schichttypen State
  const [types, setTypes] = useState<ShiftType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [showInactiveTypes, setShowInactiveTypes] = useState(false);

  // Schichtregeln State
  const [rules, setRules] = useState<ShiftRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [showInactiveRules, setShowInactiveRules] = useState(false);

  // Current Shift Status
  const [currentShift, setCurrentShift] = useState<CurrentShiftResponse | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Schicht-Einstellungen State
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSuccess, setConfigSuccess] = useState<string | null>(null);
  const [boundaryGraceMinutes, setBoundaryGraceMinutes] = useState<number>(0);
  const [boundaryGraceInput, setBoundaryGraceInput] = useState<string>('0');

  // Load Schichttypen
  const loadTypes = async () => {
    setTypesLoading(true);
    try {
      const res = await fetch('/api/admin/shift-types', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTypes(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Schichttypen:', err);
    }
    setTypesLoading(false);
  };

  // Load Schichtregeln
  const loadRules = async () => {
    setRulesLoading(true);
    try {
      const res = await fetch('/api/admin/shift-rules', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRules(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Schichtregeln:', err);
    }
    setRulesLoading(false);
  };

  // Check current shift status via API
  const checkCurrentStatus = async () => {
    setCheckingStatus(true);
    try {
      const res = await fetch('/api/shifts/current', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCurrentShift(data);
      }
    } catch (err) {
      console.error('Fehler beim Prüfen der aktuellen Schicht:', err);
    }
    setCheckingStatus(false);
  };

  // Load Schicht-Konfiguration
  const loadConfig = async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const res = await fetch('/api/shifts/config', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBoundaryGraceMinutes(data.boundaryGraceMinutes);
        setBoundaryGraceInput(String(data.boundaryGraceMinutes));
      } else {
        const err = await res.json().catch(() => ({}));
        setConfigError(err.error || 'Fehler beim Laden');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Konfiguration:', err);
      setConfigError('Verbindungsfehler');
    }
    setConfigLoading(false);
  };

  // Save Schicht-Konfiguration
  const saveConfig = async () => {
    const parsed = parseInt(boundaryGraceInput, 10);
    if (isNaN(parsed) || parsed < 0 || parsed > 60) {
      setConfigError('Wert muss zwischen 0 und 60 liegen');
      return;
    }

    setConfigSaving(true);
    setConfigError(null);
    setConfigSuccess(null);
    try {
      const res = await fetch('/api/shifts/config', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ boundaryGraceMinutes: parsed }),
      });
      if (res.ok) {
        const data = await res.json();
        setBoundaryGraceMinutes(data.boundaryGraceMinutes);
        setBoundaryGraceInput(String(data.boundaryGraceMinutes));
        setConfigSuccess('Einstellung gespeichert');
        setTimeout(() => setConfigSuccess(null), 3000);
      } else {
        const err = await res.json().catch(() => ({}));
        setConfigError(err.error || 'Fehler beim Speichern');
      }
    } catch (err) {
      console.error('Fehler beim Speichern der Konfiguration:', err);
      setConfigError('Verbindungsfehler');
    }
    setConfigSaving(false);
  };

  // Input-Handler mit Validierung
  const handleGraceInputChange = (value: string) => {
    // Nur Ziffern erlauben
    const cleaned = value.replace(/[^0-9]/g, '');
    setBoundaryGraceInput(cleaned);
  };

  // Prüfen ob sich der Wert geändert hat
  const configIsDirty = parseInt(boundaryGraceInput, 10) !== boundaryGraceMinutes;

  // Die Regel-Vorschau ist entfallen (N6): Sie zeigte, welche Regel zu einer
  // Uhrzeit greift. Im Kalender sieht man direkt, was geplant ist.

  useEffect(() => {
    loadTypes();
    loadRules();
    loadConfig();
  }, []);

  // Focus/Highlight nach Redirect von Detailseite
  useEffect(() => {
    const focus = searchParams.get('focus');
    const id = searchParams.get('id');

    if (!focus || !id) return;

    // Warten bis Daten geladen sind
    if ((focus === 'types' && typesLoading) || (focus === 'rules' && rulesLoading)) return;

    // Zur entsprechenden Sektion scrollen
    const targetRef = focus === 'types' ? typesSectionRef : rulesSectionRef;
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Eintrag highlighten
    setHighlightedId(id);

    // URL bereinigen (Query-Params entfernen)
    setSearchParams({}, { replace: true });

    // Highlight nach 2.5s entfernen
    const timer = setTimeout(() => {
      setHighlightedId(null);
    }, 2500);

    return () => clearTimeout(timer);
  }, [searchParams, typesLoading, rulesLoading, setSearchParams]);

  const filteredTypes = sortAlpha(showInactiveTypes ? types : types.filter((t) => t.isActive));
  const activeTypesCount = types.filter((t) => t.isActive).length;

  const filteredRules = showInactiveRules ? rules : rules.filter((r) => r.isActive);
  const sortedRules = [...filteredRules].sort((a, b) => {
    if (a.priority !== b.priority) return b.priority - a.priority;
    return a.name.localeCompare(b.name, 'de');
  });
  const activeRulesCount = rules.filter((r) => r.isActive).length;

  // Berechne Konflikte für jede Regel (vereinfachte Version)
  const ruleConflicts = useMemo(() => {
    const conflicts: Record<string, number> = {};
    const activeRules = rules.filter((r) => r.isActive);

    for (const rule of activeRules) {
      let conflictCount = 0;
      for (const other of activeRules) {
        if (rule.id === other.id) continue;

        // Prüfe Wochentag-Überschneidung
        const weekdayOverlap = rule.weekdays.some((d) => other.weekdays.includes(d));
        if (!weekdayOverlap) continue;

        // Prüfe Zeit-Überschneidung (vereinfacht)
        const parseTime = (t: string) => {
          const [h, m] = t.split(':').map(Number);
          return h * 60 + m;
        };
        const ruleStart = parseTime(rule.startTime);
        const ruleEnd = parseTime(rule.endTime);
        const otherStart = parseTime(other.startTime);
        const otherEnd = parseTime(other.endTime);

        const ruleIsOvernight = ruleEnd < ruleStart;
        const otherIsOvernight = otherEnd < otherStart;

        const ruleRanges = ruleIsOvernight
          ? [
              [ruleStart, 1440],
              [0, ruleEnd],
            ]
          : [[ruleStart, ruleEnd]];
        const otherRanges = otherIsOvernight
          ? [
              [otherStart, 1440],
              [0, otherEnd],
            ]
          : [[otherStart, otherEnd]];

        let timeOverlap = false;
        for (const [rs, re] of ruleRanges) {
          for (const [os, oe] of otherRanges) {
            if (rs < oe && re > os) {
              timeOverlap = true;
              break;
            }
          }
          if (timeOverlap) break;
        }

        if (timeOverlap) conflictCount++;
      }
      conflicts[rule.id] = conflictCount;
    }

    return conflicts;
  }, [rules]);

  return (
    <LeitstandRahmen titel="Schichten" benutzer={user?.displayName}>
      <div className="flex flex-col gap-6">
        <p className="font-zahl text-[12.5px] text-tinte-3">
          {activeTypesCount} Schichttypen, {activeRulesCount} Schichtregeln
        </p>

        {/* Status/Preview Section (oben) */}
        <div className="grid gap-5 lg:grid-cols-2">
          {/* Current Shift Status */}
          <Karte titel="Aktuelle Schicht" marke="Stand jetzt">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={checkCurrentStatus}
                  disabled={checkingStatus}
                  className={KNOPF_PRIMAER}
                >
                  {checkingStatus ? 'Prüft …' : 'Prüfen'}
                </button>
                {currentShift === null && (
                  <span className="text-sm text-tinte-3">
                    „Prüfen" zeigt, ob gerade erfasst werden kann.
                  </span>
                )}
              </div>

              {currentShift !== null &&
                (currentShift.hasActiveShift ? (
                  <div className="flex flex-col gap-2">
                    <Zustand art="gut">Erfassung möglich</Zustand>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                      <Feld marke="Regel">{currentShift.ruleName}</Feld>
                      <Feld marke="Typ">{currentShift.shiftTypeName}</Feld>
                      <Feld marke="Zeitfenster">
                        <span className="font-zahl tabular-nums">
                          {currentShift.startTime} - {currentShift.endTime}
                        </span>
                      </Feld>
                      <Feld marke="Schichtdatum">
                        <span className="font-zahl tabular-nums">{currentShift.shiftDateStr}</span>
                      </Feld>
                    </dl>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Zustand art="stoerung">Keine aktive Schicht</Zustand>
                    <p className="text-sm text-tinte-2">
                      Erfassung ist aktuell <strong className="text-tinte">nicht möglich</strong>.
                    </p>
                  </div>
                ))}
            </div>
          </Karte>
        </div>

        {/* Hinweis-Banner bei inaktiver Schicht */}
        {currentShift && !currentShift.hasActiveShift && (
          <Notiz titel="Hinweis zur Erfassung">
            Ohne aktive Schichtregel können keine Erfassungen zugeordnet werden. Prüfen Sie die
            Schichtregeln oder erweitern Sie die Zeitfenster.
          </Notiz>
        )}

        {/* Section: Einstellungen */}
        <Karte titel="Einstellungen" marke="gilt für alle Schichtgrenzen">
          {configLoading ? (
            <p className="py-4 text-center text-sm text-tinte-3">Lade Einstellungen …</p>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Feedback */}
              {configError && (
                <p className="rounded-[4px] border border-stoer/40 bg-flaeche-2 px-3 py-2 text-[13px] text-stoer">
                  {configError}
                </p>
              )}
              {configSuccess && (
                <p className="font-zahl text-[13px] text-lauf">✓ {configSuccess}</p>
              )}

              {/* Kulanz an Schichtgrenzen */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex-1">
                  <label htmlFor="boundaryGrace" className="block text-sm text-tinte">
                    Kulanz an Schichtgrenzen (Minuten)
                  </label>
                  <p className="mt-1 text-xs text-tinte-3">
                    Einträge kurz nach Schichtwechsel werden der vorherigen Schicht zugerechnet.
                    Gilt für alle Schichtgrenzen.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="boundaryGrace"
                    type="text"
                    inputMode="numeric"
                    value={boundaryGraceInput}
                    onChange={(e) => handleGraceInputChange(e.target.value)}
                    className={`${EINGABE} w-20 text-right font-zahl`}
                    placeholder="0"
                  />
                  <span className="text-sm text-tinte-3">min</span>
                  <button
                    onClick={saveConfig}
                    disabled={configSaving || !configIsDirty}
                    className={KNOPF_PRIMAER}
                  >
                    {configSaving ? 'Speichern …' : 'Speichern'}
                  </button>
                </div>
              </div>

              {/* Info bei deaktiviert */}
              {boundaryGraceMinutes === 0 && !configIsDirty && (
                <p className="text-xs text-tinte-3">Kulanzfenster ist ausgeschaltet (0 Minuten).</p>
              )}
            </div>
          )}
        </Karte>

        {/* Section: Schichttypen */}
        <section ref={typesSectionRef} className="scroll-mt-28">
          <Karte titel="Schichttypen" marke={`${activeTypesCount} aktiv`}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-[13px] text-tinte-2">
                  <input
                    type="checkbox"
                    checked={showInactiveTypes}
                    onChange={(e) => setShowInactiveTypes(e.target.checked)}
                    className="h-4 w-4 rounded-[3px] accent-[var(--color-akzent)]"
                  />
                  Inaktive
                </label>
                <span className="flex items-center gap-2">
                  <button onClick={loadTypes} disabled={typesLoading} className={KNOPF_LEISE}>
                    {typesLoading ? 'Lädt …' : 'Aktualisieren'}
                  </button>
                  <button
                    onClick={() => navigate('/admin/shift-types/new')}
                    className={KNOPF_PRIMAER}
                  >
                    Neuer Typ
                  </button>
                </span>
              </div>

              {typesLoading ? (
                <p className="py-8 text-center text-sm text-tinte-3">Lade Schichttypen …</p>
              ) : filteredTypes.length === 0 ? (
                <p className="py-8 text-center text-sm text-tinte-3">
                  Keine Schichttypen vorhanden
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Tabelle>
                    <thead>
                      <tr>
                        <Kopfzelle>Name</Kopfzelle>
                        <Kopfzelle rechts>Zustand</Kopfzelle>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTypes.map((type, i) => {
                        const letzte = i === filteredTypes.length - 1;
                        return (
                          <tr
                            key={type.id}
                            onClick={() => navigate(`/admin/shift-types/${type.id}`)}
                            className={`cursor-pointer transition-colors duration-300 hover:bg-flaeche-2 ${
                              highlightedId === type.id ? 'bg-akzent-tief' : ''
                            } ${type.isActive ? '' : 'opacity-60'}`}
                          >
                            <Zelle letzte={letzte}>{type.name}</Zelle>
                            <Zelle rechts letzte={letzte}>
                              <ZustandsZeichen aktiv={type.isActive} />
                            </Zelle>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Tabelle>
                </div>
              )}
            </div>
          </Karte>
        </section>
      </div>
    </LeitstandRahmen>
  );
}
